import React, { useEffect, useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography
} from '@mui/material';
import { DndContext, MeasuringStrategy, DragOverlay } from '@dnd-kit/core';
import { SortableContext, verticalListSortingStrategy } from '@dnd-kit/sortable';

import { FormField, FormLayout } from '../../types/form';
import { useFormLayoutDnd } from './useFormLayoutDnd';
import { AvailableFields } from './AvailableFields';
import { SectionRenderer } from './sectionRenderer';
import { FieldEditor } from './FieldEditor';
import { SortableFieldItem } from './SortableFieldItem';
import { RelatedListEditor, RelatedListConfig } from './RelatedListEditor';
import api from '../../services/api';

// ============================================================================
// Interfaces
// ============================================================================
interface FormLayoutEditorProps {
  open: boolean;
  onClose: () => void;
  onSave: (layout: FormLayout['layout_config']) => Promise<void>;
  fields: FormField[];
  currentLayout: FormLayout | null;
  tableName: string;
}

// ============================================================================
// Component
// ============================================================================
export const FormLayoutEditor: React.FC<FormLayoutEditorProps> = ({
  open,
  onClose,
  onSave,
  fields,
  currentLayout,
  tableName
}) => {
  const [layout, setLayout] = useState<FormLayout['layout_config']>(() => {
    
    if (!currentLayout?.layout_config) {
      return {
        fields: [],
        sections: [{ id: 'default', title: 'Main Section', order: 0, type: 'default' }],
        related_lists: []
      };
    }

    // Parse the layout_config if it's a string (from database)
    const layoutConfig = typeof currentLayout.layout_config === 'string' 
      ? JSON.parse(currentLayout.layout_config)
      : currentLayout.layout_config;

    // Ensure we have the related_lists from the layout_config
    const sections = layoutConfig.sections || [{ id: 'default', title: 'Main Section', order: 0, type: 'default' }];
    const relatedLists = layoutConfig.related_lists || [];
    
    // Make sure each related list has a corresponding section
    const relatedListSections = relatedLists.map((rl: { id: string; label: string }) => ({
      id: rl.id,
      title: rl.label,
      order: sections.length + relatedLists.indexOf(rl),
      type: 'related_list' as const
    }));
    
    // Filter out any related list sections that already exist
    const existingSectionIds = new Set(sections.map((s: { id: string }) => s.id));
    const newRelatedListSections = relatedListSections.filter((s: { id: string }) => !existingSectionIds.has(s.id));
    
    const finalLayout = {
      fields: layoutConfig.fields || [],
      sections: [...sections, ...newRelatedListSections],
      related_lists: relatedLists
    };
    
    return finalLayout;
  });

  const [availableFields, setAvailableFields] = useState<FormField[]>([]);
  const [editingField, setEditingField] = useState<FormLayout['layout_config']['fields'][0] | null>(null);
  const [editingSectionId, setEditingSectionId] = useState<string | null>(null);
  const [editingRelatedList, setEditingRelatedList] = useState<boolean>(false);
  const [availableTables, setAvailableTables] = useState<string[]>([]);

  // Track unused fields
  useEffect(() => {
    const usedFieldNames = new Set(layout.fields.map(f => f.name));
    const filtered = fields.filter(f => !usedFieldNames.has(f.field_name));
    setAvailableFields(filtered);
  }, [fields, layout.fields]);

  // Fetch available tables
  useEffect(() => {
    const fetchAvailableTables = async () => {
      try {
        const response = await api.get('/config');
        const tableNames = response.data.map((config: any) => config.table_name);
        setAvailableTables(tableNames);
      } catch (error) {
        console.error('Error fetching available tables:', error);
        // Fallback to empty array
        setAvailableTables([]);
      }
    };

    if (open) {
      fetchAvailableTables();
    }
  }, [open]);

  const {
    sensors,
    activeId,
    handleDragStart,
    handleDragOver,
    handleDragEnd,
    collisionDetection
  } = useFormLayoutDnd({
    layout,
    setLayout,
    availableFields,
    setAvailableFields
  });

  const handleSave = async () => {
    try {
      await onSave({
        fields: layout.fields,
        sections: layout.sections,
        related_lists: layout.related_lists || []
      });
      onClose();
    } catch (error) {
      console.error('Error saving layout:', error);
    }
  };

  const updateSectionTitle = (id: string, newTitle: string) => {
    setLayout(prev => ({
      ...prev,
      sections: prev.sections.map(s =>
        s.id === id ? { ...s, title: newTitle } : s
      ),
      fields: prev.fields.map(f =>
        f.sectionId === id ? { ...f, sectionTitle: newTitle } : f
      )
    }));
  };

  const handleAddSection = () => {
    const newId = `${layout.sections.length + 1}`;
    const newSection = {
      id: newId,
      title: `Section ${layout.sections.length + 1}`,
      order: layout.sections.length,
      type: 'default' as const
    };
    setLayout(prev => ({
      ...prev,
      sections: [...prev.sections, newSection]
    }));
  };

  const handleAddRelatedList = () => {
    setEditingRelatedList(true);
  };

  const handleRelatedListSave = (config: RelatedListConfig) => {
    const newId = `related-${config.id}`;
    const newSection = {
      id: newId,
      title: config.label,
      order: layout.sections.length,
      type: 'related_list' as const
    };

    setLayout(prev => ({
      ...prev,
      sections: [...prev.sections, newSection],
      related_lists: [...(prev.related_lists || []), { ...config, id: newId }]
    }));
  };

  const handleDeleteSection = (id: string) => {
    if (id === 'default') return;
    setLayout(prev => ({
      ...prev,
      sections: prev.sections.filter(s => s.id !== id),
      fields: prev.fields.filter(f => f.sectionId !== id),
      related_lists: (prev.related_lists || []).filter(r => r.id !== id)
    }));
  };

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="lg">
      <DialogTitle>Edit Layout: {tableName}</DialogTitle>
      <DialogContent>
        <DndContext
          sensors={sensors}
          collisionDetection={collisionDetection}
          measuring={{ droppable: { strategy: MeasuringStrategy.Always } }}
          onDragStart={handleDragStart}
          onDragOver={handleDragOver}
          onDragEnd={handleDragEnd}
        >
          <Box sx={{ display: 'flex', gap: 2, mt: 2, height: '600px' }}>
            <AvailableFields fields={availableFields} />

            <Box sx={{ flex: 1, overflowY: 'auto', maxHeight: 'calc(100vh - 250px)' }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
                <Typography variant="h6">Layout</Typography>
                <Box>
                  <Button onClick={handleAddSection} variant="outlined" size="small" sx={{ mr: 1 }}>
                    Add Section
                  </Button>
                  <Button onClick={handleAddRelatedList} variant="outlined" size="small">
                    Add Related List
                  </Button>
                </Box>
              </Box>

              <SortableContext items={layout.fields.map((f, index) => f.id || `field-${index}`)} strategy={verticalListSortingStrategy}>
                {layout.sections.map((section, index) => {
                  const sectionFields = layout.fields
                    .filter(f => f.sectionId === section.id)
                    .sort((a, b) => (a.order ?? 0) - (b.order ?? 0));

                  return (
                    <SectionRenderer
                      key={section.id || `section-${index}`}
                      section={section}
                      fields={sectionFields}
                      editingSectionId={editingSectionId}
                      setEditingSectionId={setEditingSectionId}
                      onChangeTitle={updateSectionTitle}
                      onDelete={handleDeleteSection}
                      onEditField={setEditingField}
                      relatedList={section.type === 'related_list' ? layout.related_lists?.find(r => r.id === section.id) : undefined}
                    />
                  );
                })}
              </SortableContext>

              <DragOverlay>
                {activeId && (
                  <SortableFieldItem
                    id={activeId}
                    field={
                      layout.fields.find(f => f.id === activeId) ??
                      {
                        id: activeId,
                        name: '',
                        label: '',
                        type: 'text',
                        width: 100,
                        required: false,
                        sectionId: '',
                        sectionTitle: ''
                      }
                    }
                    onEdit={() => { /* Edit action not needed for overlay items */ }}
                  />
                )}
              </DragOverlay>
            </Box>
          </Box>
        </DndContext>
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button variant="contained" onClick={handleSave}>Save</Button>
      </DialogActions>

      {editingField && (
        <FieldEditor
          field={editingField}
          onClose={() => setEditingField(null)}
          onUpdate={(updatedField) => {
            setLayout(prev => ({
              ...prev,
              fields: prev.fields.map(f =>
                f.id === updatedField.id ? updatedField : f
              )
            }));
            setEditingField(null);
          }}
          open
        />
      )}

      {editingRelatedList && (
        <RelatedListEditor
          open={true}
          onClose={() => setEditingRelatedList(false)}
          onSave={handleRelatedListSave}
          availableTables={availableTables}
          fields={fields}
        />
      )}
    </Dialog>
  );
};
